import { Modal, Button, Typography } from "antd";
import { useMercuryContext, useMercuryDispatch, ActionTypes } from "../../context/index";
import { extendSessionToken, getSessionRemainingMs, performAuth0Logout } from "../authHelpers";
import { beginSessionRefresh, endSessionRefresh } from "../sessionManager";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";

const { Paragraph } = Typography;
const SESSION_CHANNEL_NAME = "session-sync";

// leader tab broadcasts extend or expire so follower tabs react locally without sharing tokens.
function broadcastSessionDecision(type: "SESSION_EXTEND" | "SESSION_EXPIRE"): void {
    if (globalThis.BroadcastChannel === undefined) {
        return;
    }

    const tabId = globalThis.sessionStorage.getItem("mercury:session-tab-id") || "session-modal";
    const channel = new globalThis.BroadcastChannel(SESSION_CHANNEL_NAME);

    try {
        channel.postMessage({
            type,
            tabId,
            timestamp: Date.now(),
        });
    } finally {
        channel.close();
    }
}

export function formatRemaining(remainingMs: number): string {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

// Session warning modal. Opens shortly before the MAS token expires and lets the
// user extend or end the session. UMS only refreshes tokens that are still
// valid, so Extend is withdrawn the moment the countdown reaches zero; the
// session manager then logs every tab out after its grace window.
const SessionExpiredModal = () => {
    const context = useMercuryContext();
    const dispatch = useMercuryDispatch();
    const [isExtending, setIsExtending] = useState(false);
    const [remainingMs, setRemainingMs] = useState<number | null>(null);

    const visible = context?.sessionExpired === true;
    const accessToken = context?.accessToken;
    const expired = remainingMs !== null && remainingMs <= 0;

    // Countdown driven off the freshest token (cookie or in-memory), so a
    // refresh landing in another tab is reflected here without a reload.
    useEffect(() => {
        if (!visible) {
            setRemainingMs(null);
            return;
        }

        const update = () => setRemainingMs(getSessionRemainingMs(accessToken));
        update();
        const intervalId = setInterval(update, 1000);
        return () => clearInterval(intervalId);
    }, [visible, accessToken]);

    const handleExtend = async () => {
        if (isExtending || expired) {
            return;
        }

        if (!beginSessionRefresh()) {
            // The leader tab's auto refresh is already in flight; let it finish.
            return;
        }
        setIsExtending(true);

        try {
            const token = await extendSessionToken(accessToken);
            console.log("[SessionExpired] Token received:", token ? token.substring(0, 20) + "..." : "null");

            if (!token) {
                throw new Error("No token received from MAS refresh endpoint");
            }

            context.setToken(token);

            const cookieAfterSet = Cookies.get("mercury_shared_token");
            console.log("[SessionExpired] Cookie after setToken:", cookieAfterSet ? cookieAfterSet.substring(0, 20) + "..." : "null");

            // clear session expired flag
            dispatch({ type: ActionTypes.SESSION_RESTORED });
            broadcastSessionDecision("SESSION_EXTEND");

            // clear session error filter highlighting and keep filter values
            globalThis.dispatchEvent(new Event("mercury:session-restored"));

        } catch (e: any) {
            console.warn("[SessionExpired] Session extend encountered an error", e);

            if (e?.code === "MAS_TOKEN_REFRESH_FAILED" || e?.code === "NO_MAS_TOKEN") {
                // Refresh denied outright (expired or invalid token): only a
                // fresh Auth0 login can recover. Leave the SSO session alive so
                // LoginPage re-authenticates silently and mints a new MAS token;
                // don't strand the user on a modal whose Extend button can never
                // succeed again.
                context.removeToken();
                globalThis.location.assign("/");
                return;
            }
            // Transient failures (network etc.) keep the modal open for retry.
        } finally {
            endSessionRefresh();
            setIsExtending(false);
        }
    };

    const handleLogout = () => {
        broadcastSessionDecision("SESSION_EXPIRE");

        try {
            // Redirect to Auth0 first; local cookie/session cleanup happens on
            // the /logout return leg (LogoutRedirect). Tearing down state before
            // navigating lets LoginPage mount and silently re-authenticate.
            performAuth0Logout();
        } catch (e) {
            console.error("Auth0 logout failed, fallback to local logout", e);
            context.removeToken();
            Cookies.set("logged_out", "true", { path: "/" });
            globalThis.location.href = "/logout";
        }
    };

    const footer = expired
        ? [
            <Button key="logout" type="primary" onClick={handleLogout}>
                Log in again
            </Button>,
        ]
        : [
            <Button key="logout" onClick={handleLogout}>
                End session
            </Button>,
            <Button key="extend" type="primary" onClick={handleExtend} loading={isExtending} disabled={isExtending}>
                Extend session
            </Button>,
        ];

    return (
        <Modal
            open={visible}
            closable={false}
            maskClosable={false}
            title={expired ? "Session expired" : "Session expiring soon"}
            footer={footer}
        >
            {expired ? (
                <Paragraph>
                    Your session has expired and can no longer be extended. You
                    will be signed out shortly; log in again to continue working.
                </Paragraph>
            ) : (
                <Paragraph>
                    Your session will expire in{" "}
                    <strong>{remainingMs === null ? "a moment" : formatRemaining(remainingMs)}</strong>.
                    Extend your session to continue working, or end your session
                    and log out.
                </Paragraph>
            )}
        </Modal>
    );
};

export default SessionExpiredModal;
